/* Owns the Ctrl+K palette of PANEL.md §4. Three sections in a single listbox:
   the `GET /v1/lookup` identifier search, the sidebar routes of the active
   server, and the server switcher list. One roving-focus listbox rather than
   three, so ArrowDown crosses a group boundary the way every command palette a
   user has met already behaves; the groups carry `role="group"` for the label. */

import { useQuery } from '@tanstack/react-query'
import { Search, Server as ServerIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { cn } from '../components/ui/cn.js'
import { useFocusTrap } from '../components/ui/useFocusTrap.js'
import { useT } from '../i18n/index.js'
import { useAuthState } from '../lib/auth-store.js'
import { parsePaletteInput } from '../lib/identifiers.js'
import { useRole } from '../lib/permissions.js'
import { lookupQueryOptions } from './api.js'
import { visibleServerNav } from './Sidebar.js'

const DEBOUNCE_MS = 300

interface PaletteOption {
  id: string
  groupKey: string
  label: string
  hint?: string
  to: string
}

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps): ReactNode {
  const t = useT()
  const navigate = useNavigate()
  const { servers, activeServerId } = useAuthState()
  const role = useRole(activeServerId)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const close = useCallback(() => onOpenChange(false), [onOpenChange])
  const containerRef = useFocusTrap<HTMLDivElement>({ open, onClose: close })

  useEffect(() => {
    if (!open) return
    setQuery('')
    setDebounced('')
    setActiveIndex(0)
  }, [open])

  useEffect(() => {
    // 300 ms: the lookup route is capped at 60/min per user, and a keystroke is
    // not a search intent.
    const handle = setTimeout(() => setDebounced(query), DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const parsed = useMemo(() => parsePaletteInput(debounced), [debounced])
  const expression = parsed?.kind === 'identifier' ? parsed.expression : ''

  const lookup = useQuery({ ...lookupQueryOptions(expression), enabled: open && expression !== '' })

  const options = useMemo<PaletteOption[]>(() => {
    const result: PaletteOption[] = []

    // A `SAC-` code is a ban code, not an identifier: it belongs in the bans
    // search of the active server, which is where the operator can act on it.
    if (parsed?.kind === 'banCode' && activeServerId) {
      result.push({
        id: `ban-${parsed.code}`,
        groupKey: 'ban.banCode',
        label: parsed.code,
        to: `/s/${activeServerId}/bans?q=${encodeURIComponent(parsed.code)}`,
      })
    }

    for (const hit of lookup.data?.players ?? []) {
      result.push({
        id: `player-${hit.serverId}-${hit.player.id}`,
        groupKey: 'player.lookupTitle',
        label: hit.player.name,
        hint: hit.serverName,
        to: `/s/${hit.serverId}/players/${hit.player.id}`,
      })
    }

    const needle = query.trim().toLocaleLowerCase()
    if (activeServerId) {
      for (const item of visibleServerNav(role, activeServerId)) {
        const label = t(item.labelKey)
        if (needle && !label.toLocaleLowerCase().includes(needle) && !item.path.includes(needle)) continue
        result.push({ id: `page-${item.path}`, groupKey: 'nav.pages', label, to: item.to })
      }
    }

    for (const server of servers) {
      if (needle && !server.name.toLocaleLowerCase().includes(needle)) continue
      result.push({
        id: `server-${server.id}`,
        groupKey: 'nav.servers',
        label: server.name,
        hint: t(`common.status.${server.status}`),
        to: `/s/${server.id}/dashboard`,
      })
    }

    return result
  }, [parsed, activeServerId, lookup.data, query, role, servers, t])

  useEffect(() => {
    setActiveIndex(0)
  }, [options.length])

  if (!open) return null

  const go = (option: PaletteOption): void => {
    close()
    void navigate(option.to)
  }

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (options.length === 0 ? 0 : (index + 1) % options.length))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (options.length === 0 ? 0 : (index - 1 + options.length) % options.length))
      return
    }
    if (event.key === 'Enter') {
      const option = options[activeIndex]
      if (option) {
        event.preventDefault()
        go(option)
      }
    }
  }

  // Rendered as a flat index map so the roving focus is one sequence while the
  // markup still nests each group under its own label.
  let cursor = 0
  const groups: Array<{ key: string; entries: Array<{ option: PaletteOption; index: number }> }> = []
  for (const option of options) {
    const last = groups[groups.length - 1]
    const entry = { option, index: cursor }
    cursor += 1
    if (last && last.key === option.groupKey) last.entries.push(entry)
    else groups.push({ key: option.groupKey, entries: [entry] })
  }

  const listboxId = 'command-palette-listbox'
  const activeOption = options[activeIndex]
  const activeOptionId = activeOption ? `palette-option-${activeOption.id}` : undefined

  return (
    <div className="fixed inset-0 z-50" data-testid="command-palette">
      <div className="absolute inset-0 bg-black/60" onClick={close} aria-hidden />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('common.commandPalette')}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="absolute inset-x-0 top-16 mx-auto w-[min(640px,92vw)] overflow-hidden rounded-lg border border-border-strong bg-elevated shadow-pop"
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search aria-hidden className="size-4 shrink-0 text-text-faint" />
          <input
            type="search"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('player.lookupPlaceholder')}
            aria-label={t('common.search')}
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            className="w-full bg-transparent py-1 text-base text-text placeholder:text-text-faint focus:outline-none"
          />
        </div>

        <ul id={listboxId} role="listbox" aria-label={t('common.commandPalette')} className="max-h-80 overflow-y-auto p-1">
          {options.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-text-faint">
              {lookup.isFetching ? t('common.loading') : t('common.noResults')}
            </li>
          ) : (
            groups.map((group) => (
              <li key={`${group.key}-${group.entries[0]?.index ?? 0}`}>
                <div role="group" aria-label={t(group.key)}>
                  <div className="px-3 pt-2 pb-1 text-xs font-medium tracking-wide text-text-faint uppercase">
                    {t(group.key)}
                  </div>
                  <ul>
                    {group.entries.map(({ option, index }) => (
                      <li key={option.id}>
                        <button
                          type="button"
                          role="option"
                          id={`palette-option-${option.id}`}
                          aria-selected={index === activeIndex}
                          tabIndex={-1}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => go(option)}
                          className={cn(
                            'flex w-full min-h-11 items-center gap-2 rounded-md px-3 text-left text-sm md:min-h-9',
                            index === activeIndex ? 'bg-panel text-text' : 'text-text-muted hover:bg-panel',
                          )}
                        >
                          {option.groupKey === 'nav.servers' ? (
                            <ServerIcon aria-hidden className="size-4 shrink-0 text-text-faint" />
                          ) : null}
                          <span className="flex-1 truncate">{option.label}</span>
                          {option.hint ? <span className="text-xs text-text-faint">{option.hint}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}
